<template>
  <div id="cover" :class="'version-' + version">
    <template v-if="config">
      <section class="cover__head" :style="headerStyle">
        <div class="cover__head__title" v-html="config.cover.title[lng]" />
        <div v-if="config.cover.description" class="cover__head__desc" v-html="config.cover.description[lng]" />
        <a class="cover__scroll-down" @click="scrollDown" />
      </section>
      <section class="cover__outstanding">
        <div class="cover__section__inner">
          <div class="cover__outstanding-title">
            <a
              :class="['cover__outstanding-title__link', { active: outstanding === 'highlights' }]"
              @click="outstanding = 'highlights'"
            >Highlights</a>
            <div class="cover__outstanding-title__link-separator">/</div>
            <a
              :class="['cover__outstanding-title__link', { active: outstanding === 'populars' }]"
              @click="outstanding = 'populars'"
            >Popular</a>
            <router-link
              class="cover__outstanding-explore-all"
              :to="exploreURL"
            >
              Explore all indicators
            </router-link>
          </div>
          <div class="cover__outstanding-content">
            <template v-if="outstanding === 'highlights'">
              <div
                v-for="(highlight, i) in config.highlights"
                :key="i"
                class="cover__outstanding-item"
              >
                <div class="cover__outstanding-item__text">
                  <div class="cover__outstanding-item__title">
                    <v-clamp :max-lines="2" autoresize>{{ highlight.title[lng] }}</v-clamp>
                  </div>
                  <router-link
                    v-if="!isMobile"
                    :to="parseAsExplore(highlight.graphURL)"
                    class="cover__outstanding-item__view-more"
                  >
                    View more
                  </router-link>
                </div>
                <div
                  class="cover__outstanding-item__chart"
                  :style="'background-image: url(' + getHighlightURL(highlight.imageId) + ')'"
                />
                <router-link
                  v-if="isMobile"
                  class="cover__outstanding-item__view-more"
                  :to="parseAsExplore(highlight.graphURL)"
                >
                  View more
                </router-link>
              </div>
            </template>
            <template v-else>
              <div
                v-for="(popular, i) of populars.slice(0, 3)"
                :key="i"
                class="cover__outstanding-item"
              >
                <div class="cover__outstanding-item__text">
                  <div class="cover__outstanding-item__title">
                    <v-clamp autoresize :max-lines="2">
                      {{
                        (popular.label && popular.label[lng]) || ''
                      }}
                    </v-clamp>
                  </div>
                  <router-link
                    class="cover__outstanding-item__view-more"
                    :to="parsePopularURL(popular)"
                  >
                    View more
                  </router-link>
                </div>
                <div
                  class="cover__outstanding-item__chart"
                  :style="'background-image: url(' + getHighlightURL(popular.imageId) + ')'"
                />
                <router-link
                  v-if="isMobile"
                  class="cover__outstanding-item__view-more"
                  :to="parsePopularURL(popular)"
                >
                  View more
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </section>
      <section
        v-for="(section, i) in config.sections"
        :key="i"
        class="cover__section"
      >
        <div class="cover__section__inner">
          <div class="cover__section__title">{{ section.title[lng] }}</div>
          <div class="cover__chart">
            <iframe :src="section.graphURL" loading="lazy" />
            <router-link
              class="cover__explore"
              :to="parseAsExplore(section.graphURL)"
            >
              Explore data
            </router-link>
          </div>
        </div>
      </section>
      <section class="cover__section tag-section">
        <div class="cover__section__inner">
          <p>Explore the full range of indicators on the Data Kitchen</p>
          <div class="cover__tags-selector">
            <a
              v-for="tag in firstLevelTags"
              :key="tag.id"
              class="cover__tag"
              @click="openPreselected(tag)"
            >
              <div class="icon" :style="{ backgroundImage: 'url(' + tag.imageURL + ')' }" />
              <label>{{ tag.label[lng] }}</label>
            </a>
          </div>
          <div class="cover__go-explore">
            <router-link
              class="cover__tags-explore-all"
              :to="exploreURL"
            >
              Explore our digital and interconnected world
            </router-link>
          </div>
        </div>
        <div v-if="version == 3" class="cover-logos">
          <img
            src="@/assets/images/DIT_logo.png"
            alt="logo DIT"
            loading="lazy"
            class="logo"
          >
        </div>
      </section>
    </template>
    <template v-else>
      <div class="cover__loader app-overlay">
        <div class="text">LOADING</div>
      </div>
    </template>
  </div>
</template>

<script>
import VClamp from 'vue-clamp'

export default {
  components: {
    'v-clamp': VClamp,
  },
  data() {
    return {
      outstanding: 'highlights',
    }
  },
  computed: {
    config() {
      return this.$store.state.external.frontPage
    },
    version() {
      return this.$route.params.version
    },
	  versionName() {
		  return this.$route.params.versionName
	  },
    popularityRanking() {
      return this.$store.state.external.popularityRanking
    },
    populars() {
      return this.popularityRanking
        .map(popular => ({ ...popular, ...this.$store.getters.treeIndicator(popular.id) }))
        .filter(d => d.metaId)
    },
    exploreURL() {
      return `/explorer/${this.version}/${this.versionName}/indicator/explore/${this.lng || 'usr'}`
    },
    firstLevelTags() {
      return this.$store.getters
        .tagType('firstLevel')
        .map(tag => {
          return {
            label: tag.tagName,
            id: tag.id,
            imageURL: tag.imageURL ? `${process.env.VUE_APP_CMS_IMAGE_URL}/${tag.imageURL}` : '' ,
            group: tag.parent?.tagName,
          }
        }).sort((ind1, ind2) => {
          return ind1.label[this.lng].toLowerCase() < ind2.label[this.lng].toLowerCase() ? -1 : 1
        })
    },
    backgroundImage() {
      if (!this.config || !this.config.cover || !this.config.cover.backgroundImage) return null

      return `url('${process.env.VUE_APP_CMS_IMAGE_URL}/${this.config.cover.backgroundImage}')`
    },
    headerStyle() {
      if (!this.config || !this.config.cover) return {}
      if(this.config.cover.backgroundImage){
        return {
          backgroundImage: this.backgroundImage
        }
      } else {
        return {
          background: this.coverGradient
        }
      }
    },
    coverGradient() {
      if (!this.config) return 'linear-gradient(to bottom, #4283e3 40%, #222)'
      const color = this.config.cover.backgroundGradientColor.replace(/^#/, '')
      const darker = color.split('').reduce((acum, char, i) => {
        acum[Math.floor(i / 2)] += char
        return acum
      }, ['', '', '']).map(char => parseInt(char, 16))
      .map(v => Math.round(v * .3))
      .map(v => v.toString(16))
      .map(c => c.length === 1 ? '0'+c : c)
      .join('')

      return `linear-gradient(to bottom, #${color} 40%, #${darker})`
    },
    sectionGradient() {
      if (!this.config) return 'linear-gradient(to bottom, #faf9f836 0, #3c76cd2e 7.29%, #faf9f836 100%)'
      const coverColor = this.config.cover.backgroundGradientColor.replace(/^#/, '')
      const color = this.getHexBands(coverColor)
        .map(v => Math.round(v + (255 - v) * 0.8))
        .map(v => v.toString(16))
        .join('')
      const lighter = this.getHexBands(coverColor)
        .map(v => Math.round(v + (255 - v) * .98))
        .map(v => v.toString(16))
        .map(c => c.length === 1 ? '0'+c : c)
        .join('')

      return `linear-gradient(to bottom, #${lighter} 0, #${color} 7.29%, #${lighter} 100%)`
    },
  },
  beforeMount() {
    this.wipeData()
    this.$store.dispatch('getPopularityRanking', this.version).then(() => {
      this.$store.dispatch('getFrontPage', this.version)
    })
  },
  mounted() {
    if (process.env.NODE_ENV !== 'production') {
      for (let iframe of this.$el.querySelectorAll('iframe')) {
        iframe.contentWindow.location.href = iframe.src + '&_=' + Date.now();
      }
    }
  },
  methods: {
    getHexBands(color) {
		return color.split('').reduce((acum, char, i) => {
			acum[Math.floor(i / 2)] += char
			return acum
		}, ['', '', ''])
		.map(c => parseInt(c, 16))
	},
    parsePopularURL(popular) {
      return `/explorer/${this.version}/${this.versionName}/indicator/explore/${this.lng || 'usr'}?mainIndId=${popular.metaId}&mainCubeId=${popular.cube.cubeMetaId}`
    },
    parseAsExplore(url) {
      const replaceRegex = new RegExp(`^.*${location.pathname}/?#`)
      return ((url && url.replace(/(embedded|thumbnail|print)/, 'explore')) || '').replace(replaceRegex, '')
    },
    scrollDown() {
      const firstSection = this.$el.querySelector('.cover__outstanding')
      const app = document.getElementById('app')
      app.scrollTo({
        top: firstSection.getBoundingClientRect().top,
        behavior: 'smooth',
      })
    },
    getHighlightURL(imgId) {
      const baseURL = process.env.VUE_APP_API_URL
      if (process.env.VUE_APP_API_MODE === 'static') {
        return `${baseURL}/oecd-highlight.png`
      } else {
        return `${baseURL}/getImage?imageId=${imgId}`
      }
    },
    getPopularURL(imgId) {
      const baseURL = process.env.VUE_APP_API_URL
      if (process.env.VUE_APP_API_MODE === 'static') {
        return `${baseURL}/oecd-highlight.png`
      } else {
        return `${baseURL}/getImage?imageId=${imgId}`
      }
    },
    wipeData() {
      this.$store.commit('wipeMeta');
      this.$store.commit('wipeData');
      this.$store.commit('rebootQuery');
    },
    openPreselected(tag) {
      this.$store.commit('preselection', tag.id) 
      this.$router.push(this.exploreURL)
    }
  },
}
</script>

<style lang="stylus">
.cover__head
  align-items: center
  background: linear-gradient(to bottom, #4283e3 40%, #222)
  background-size: cover
  display: flex
  height: 100vh
  justify-content: center
  position: relative
  flex-direction: column

  .version-1 &
   height: calc(100vh - 102px)

   @media screen and (max-width: 1024px)
     height: calc(100vh - 64px)

.cover__head__title
  m-font-size(70, 82)
  m-letter-spacing(5)
  max-width: vw(1200px)
  text-align: center
  color: #ffffff

  .mobile &
    m-font-size(40, 50)
    max-width: 90%

    +m-breakpoint(landscape)
      m-font-size(20, 25)

.cover__head__desc
  m-font-size(34, 45)
  m-letter-spacing(5)
  max-width: vw(1200px)
  text-align: center
  color: #E0E0E0

  .mobile &
    m-font-size(20, 30)
    max-width: 90%

.cover__jumbo
  width: 100%

.cover__section__inner
  margin-left: auto
  margin-right: auto
  max-width: vw(1280px)
  width: 100%

  p
    color:$blue
    opacity 1

  .cover__outstanding &
    display: flex
    flex-direction: column
    height: 100%

  .cover__section &
    padding: vh(30px) vw(60px)

    .mobile &
      padding: vh(20px) vw(20px)

  &:last-child
    padding-bottom: vw(60px)

.cover__scroll-down
  m-icon("chevron-scroll", 64 24)
  bottom: vh(80px)
  display: block
  left: 50%
  position: absolute
  transform: translateX(-50%)

  .mobile &
    m-icon("chevron-scroll", 50)

    +m-breakpoint(landscape)
      bottom: vh(10px)
      m-icon("chevron-scroll", 30)

.cover__outstanding
  background-color: #fff
  border-bottom: 1px solid $gray
  box-shadow: 1px 1px 5px 0 rgba(#000, 0.05)
  color: $blue
  margin-bottom vh(40px)
  padding: vw(10px) vw(20px)  0  /* vh(35px) */

  .mobile &
    padding: vw(10px)

  .cover__section__inner
    padding-bottom: vw(10px)

.cover__outstanding-title
  align-items: center
  display: flex
  margin-bottom: vh(25px)

  .mobile &
    margin-bottom: vw(10px)

.cover__outstanding-title__link
  color: $gray
  display: block

  .mobile &
    font-size: 0.8em

  &.active
    color: $blue

.cover__outstanding-title__link-separator
  color: $blue
  margin-left: vw(10px)
  margin-right: vw(10px)

.cover__outstanding-explore-all
  margin-left: auto
  color: $blue-2

  &:hover
    border-bottom: 1px solid

  .mobile &
    font-size: .8em

.cover__outstanding-content
  align-items: center
  display: flex
  flex: 1 1 0%
  margin-left: vw(-40px)
  margin-right: vw(-40px)
  overflow: hidden
  justify-content: space-between;

  .mobile &
    margin-left: vw(-10px)
    margin-right: vw(-10px)

.cover__outstanding-item
  flex: 0 0 33%
  height: 100%
  padding-left: vw(40px)
  padding-right: vw(40px)

  .mobile &
    flex-direction: column
    padding-left: vw(20px)
    padding-right: vw(20px)

  &:not(:last-child)
    border-right: 1px solid $gray

.cover__outstanding-item__text
  display: flex
  flex: 1 1 0%
  position: relative
  height: 3rem

.cover__outstanding-item__title
  font-weight: 700
  margin-right 15px
  font-size: .85em
  line-height: 1.3

  .mobile &
    font-size: 0.65em
    line-height: 1.2
    margin-bottom: vw(5px)

.cover__outstanding-item__view-more
  color: $blue-2
  font-family: 'Bernini Sans', 'Helvetica Neue', Helvetica, Arial, serif
  margin-left: auto
  white-space: nowrap
  font-size: 0.8em
  line-height: 1.3

  &:hover
    text-decoration: underline

  .mobile &
    font-size: .5em
    line-height: 1.8

.cover__outstanding-item__chart
  background-position: center center
  background-repeat: no-repeat
  background-size: contain
  height: vh(150px)

  .desktop &
    flex: 1 1 0%

  .mobile &
    margin-bottom: vw(5px)
    height: vh(100px)

.cover__section
  position: relative

  &.tag-section

    p
      font-size: 1.2em
      color: $blue

      .mobile &
        text-align: center

.cover__section__title
  m-font-size(28, 38)
  m-letter-spacing(5)
  color: $blue-2
  margin-bottom: vh(30px)
  text-align: center
  width 70%
  margin auto

  .mobile &
    width 80%
    m-font-size(20, 26)
    font-size: 1.1em
    line-height: 1.5
    margin-bottom: vh(30px)

.cover__chart
  position: relative
  text-align: center

  .cover__explore
    display inline-block
    m-font-size(16, 20)
    color: $blue-2
    text-align: center
    margin-top vh(40px)
    margin-bottom vh(30px)
    padding: .5em 3.5em
    background: white
    border: 1px solid #E9EAEB
    box-sizing: border-box
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.05)
    border-radius: 20px

    &:hover
      color: $blue

    .mobile &
      right: .8em
      top: .4em
      font-size: 0.8em
      line-height: 1.35

  iframe
    border none
    background-color: rgba(#fff, 0.5)
    border-radius: 5px
    box-shadow: 0px 7px 10px rgba(0, 0, 0, 0.1)
    height: 700px
    max-height: 90vh
    width: 100%
    margin-top 20px

    .mobile &
      height: 150vw

.cover__tags-selector
  flex: 1
  display: flex
  justify-content: center
  align-items: center
  flex-wrap: wrap
  background-color: rgba(255, 255, 255, .5)
  border-radius: 5px
  padding: vw(20px) 0px

.cover__tag
  padding: .5em .5em .75em
  border-radius: 50%
  display: flex
  flex-direction: column
  align-items: center
  margin: .5em
  width: 180px
  height: 120px
  border: 1px solid transparent

  .mobile &
    padding: .15em .35em
    margin: .2em
    width: "calc(30vw - calc(%s / 3))" % vw(40px)
    height: 90px

  > .icon
    width: 65px
    height: 65px
    background-repeat: no-repeat
    background-size: contain
    background-position: center

    .mobile &
      width: 25px
      height: 25px

  > label
    padding-top: .5em
    font-size: .9em
    opacity: .7
    line-height: 1.2
    text-align: center
    margin: 0 .4em 0 .5em

    .mobile &
      font-size: .7em
      line-height: 1.4

.cover__go-explore
  display: flex
  justify-content: center
  margin-top: vw(60px)

  .mobile &
    margin-top: vw(10px)
    font-size: 0.8em
    line-height: 1.4

  > a
    color: $blue-2
    padding: .5em 1.8em
    background: white
    border: 1px solid #E9EAEB;
    box-sizing: border-box;
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.05);
    border-radius: 20px;
    text-align: center

    &:hover
      color: $blue

.cover__loader
  background-image: url("~@/assets/images/oecd-loader.gif")
  background-size: 100px

  .text
    padding-top: 290px

.cover-logos
  text-align: center
  margin: 20px
  img.logo
    margin: 0 auto
    height: auto
    width: 200px
</style>
